import {storeInstance} from '@aofl/store/dist/legacy';
import {
  creditedProducts,
  nodeStatuses,
  products,
  sdoNs,
} from '@modules/constants-enumerate';
import {deepAssign} from '@aofl/object-utils';

const getStatusesToShow = (product) => {
  const base = [
    nodeStatuses.NOT_STARTED,
    nodeStatuses.MASTERY_ACHIEVED,
    nodeStatuses.PRIOR_KNOWLEDGE,
    nodeStatuses.CREDITED,
    nodeStatuses.IN_PROGRESS,
  ];
  if (!creditedProducts.includes(product)) {
    return base.filter((s) => s !== nodeStatuses.CREDITED);
  }
  return base;
};

const sdo = {
  namespace: sdoNs.PARENT_DASHBOARD,
  mutations: {
    init() {
      return {
        students: [],
        dashboardIsVisible: {
          [products.ABCMOUSE]: false,
          [products.READINGIQ]: false,
          [products.MATH_ACADEMY]: true,
          [products.READING_MASTERY]: false,
          [products.ADVENTURE_ACADEMY]: false,
          [products.ABCMOUSE_ENGLISH]: false,
          [products.MY_MATH_ACADEMY]: true,
          [products.MY_READING_ACADEMY]: true,
          [products.MY_READING_ACADEMY_ES]: true,
        },
        selectedStudent: {
          first_name: '',
          products: [],
          selectedProduct: '',
        },
        gradeIsCollapsedMap: {},
        isStatusCollapsedInGrade: {},
        stats: {
          gradeOrder: [],
          progressByGrade: {},
          totalSkillCount: 0,
          recommendedSkills: [],
          target: 3600,
          timeSpent: {}
        },
        statusesToShow: getStatusesToShow(null),
      };
    },
    setSelectedProduct(subState, product) {
      const selectedStudent = {...subState.selectedStudent};
      selectedStudent.selectedProduct = product;
      return {
        ...subState,
        selectedStudent,
        statusesToShow: getStatusesToShow(product),
      };
    },
    setStatusesToShow(subState, statusesToShow) {
      const {isStatusCollapsedInGrade, gradeIsCollapsedMap} = subState;

      const isStatusCollapsed = {
        [nodeStatuses.NOT_STARTED]: true,
        [nodeStatuses.MASTERY_ACHIEVED]: true,
        [nodeStatuses.PRIOR_KNOWLEDGE]: true,
        [nodeStatuses.CREDITED]: true,
        [nodeStatuses.IN_PROGRESS]: true,
      };
      statusesToShow.map((status) => {
        isStatusCollapsed[status] = false;
      });
      const shouldGradeBeCollapsed = statusesToShow.length === 0;

      for (const [grade, _] of Object.entries(isStatusCollapsedInGrade)) {
        gradeIsCollapsedMap[grade] = shouldGradeBeCollapsed;
        for (const [status, isCollapsed] of Object.entries(isStatusCollapsed)) {
          isStatusCollapsedInGrade[grade][status] = isCollapsed;
        }
      }
      return {
        ...subState,
        statusesToShow,
        isStatusCollapsedInGrade: {...isStatusCollapsedInGrade},
        gradeIsCollapsedMap: {...gradeIsCollapsedMap}
      };
    },
    setStats(subState, payload) {
      const {isStatusCollapsedInGrade} = subState;
      let totalSkillCount = 0;
      const gradeOrder = payload.grade_order;
      const progressByGrade = {};
      const gradeIsCollapsedMap = {};
      const statuses = Object.values(nodeStatuses);
      payload.grade_order.map((grade, index) => {
        if (!isStatusCollapsedInGrade[grade]) {
          isStatusCollapsedInGrade[grade] = {};
        }
        statuses.map((status) => {
          isStatusCollapsedInGrade[grade][status] = true;
        });
        gradeIsCollapsedMap[grade] = true;
      });
      payload.progress_by_grade.map((progress) => {
        let countWithProgress = 0;
        Object.values(progress.progress_in_grade).map((skillsPerStatus) => {
          countWithProgress += skillsPerStatus.length;
        });
        progressByGrade[progress.grade] = {
          ...progress,
          countWithProgress
        };
        totalSkillCount += progress.skill_total;
      });
      const stats = {
        gradeOrder,
        progressByGrade,
        totalSkillCount,
        timeSpent: payload.time_spent,
        target: payload.target,
        recommendedSkills: payload.recommended_skills
      };
      return {
        ...subState,
        stats,
        gradeIsCollapsedMap: {...gradeIsCollapsedMap}
      };
    },
    toggleGradeCollapse(subState, grade) {
      const {statusesToShow, gradeIsCollapsedMap, isStatusCollapsedInGrade} = subState;
      gradeIsCollapsedMap[grade] = !gradeIsCollapsedMap[grade];

      statusesToShow.map((status) => {
        isStatusCollapsedInGrade[grade][status] = false;
      });
      return {
        ...subState,
        gradeIsCollapsedMap: {...gradeIsCollapsedMap},
        isStatusCollapsedInGrade: {...isStatusCollapsedInGrade},
      };
    },
    toggleGradeAndStatusCollapse(subState, payload) {
      const isStatusCollapsedInGrade = {...subState.isStatusCollapsedInGrade};

      if (typeof isStatusCollapsedInGrade[payload.grade] === 'undefined') {
        isStatusCollapsedInGrade[payload.grade] = {};
      }

      isStatusCollapsedInGrade[payload.grade][payload.status] = !isStatusCollapsedInGrade[payload.grade][payload.status];
      return {
        ...subState,
        isStatusCollapsedInGrade
      };
    },
    setSelectedStudent(subState, payload) {
      const selectedStudent = {...payload};
      selectedStudent.selectedProduct = null;
      selectedStudent.products = [];
      payload.products.map((product) => {
        if (subState.dashboardIsVisible[product]) {
          selectedStudent.products.push(product);
        }
      });
      selectedStudent.selectedProduct = selectedStudent.products[0] || null;

      return {
        ...subState,
        selectedStudent,
        statusesToShow: getStatusesToShow(selectedStudent.selectedProduct),
      };
    },
    setProductLoaded(subState, product) {
      const productsLoading = {...subState.productsLoading};
      productsLoading[product] = false;
      return {
        ...subState,
        productsLoading
      };
    }
  },
  decorators: [
    (nextState) => { // $targetInMinutes
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.PARENT_DASHBOARD];
      const subState = state[sdoNs.PARENT_DASHBOARD];
      if (typeof nextSubState.$targetInMinutes === 'undefined' ||
        nextSubState.stats.target !== subState.stats.target) {
        nextState = deepAssign(nextState, sdoNs.PARENT_DASHBOARD, {
          $targetInMinutes: Math.floor(nextSubState.stats.target / 60),
        });
      }
      return nextState;
    },
    (nextState) => { // $gradeHasActivity, progressByGradeCount, $completedSkillsCount, $hasActivity, $priorKnowledgeSkillCount
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.PARENT_DASHBOARD];
      const subState = state[sdoNs.PARENT_DASHBOARD];
      if (
        typeof nextSubState.$gradeHasActivity === 'undefined' ||
        nextSubState.stats.progressByGrade !== subState.stats.progressByGrade
      ) {
        const $gradeHasActivity = {};
        const $progressByGradeCount = {};
        const $completedSkillsCount = {
          [nodeStatuses.MASTERY_ACHIEVED]: 0,
          total: 0
        };
        // const startOfYearDate = startOfYear();
        // const startOfYearTimestamp = startOfYearDate.getTime();

        let $hasActivity = false;
        Object.values(nextSubState.stats.progressByGrade).map((progress) => {
          $gradeHasActivity[progress.grade] = false;
          if (
            progress.progress_in_grade[nodeStatuses.IN_PROGRESS]?.length ||
              progress.progress_in_grade[nodeStatuses.MASTERY_ACHIEVED]?.length ||
              progress.progress_in_grade[nodeStatuses.PRIOR_KNOWLEDGE]?.length ||
              progress.progress_in_grade[nodeStatuses.CREDITED]?.length
          ) {
            $gradeHasActivity[progress.grade] = true;
            $hasActivity = true;
          }

          if (typeof $progressByGradeCount[progress.grade] === 'undefined') {
            $progressByGradeCount[progress.grade] = {};
          }

          $progressByGradeCount[progress.grade][nodeStatuses.IN_PROGRESS] = progress.progress_in_grade[nodeStatuses.IN_PROGRESS]?.length || 0;
          $progressByGradeCount[progress.grade][nodeStatuses.MASTERY_ACHIEVED] = progress.progress_in_grade[nodeStatuses.MASTERY_ACHIEVED]?.length || 0;
          $progressByGradeCount[progress.grade][nodeStatuses.PRIOR_KNOWLEDGE] = progress.progress_in_grade[nodeStatuses.PRIOR_KNOWLEDGE]?.length || 0;
          $progressByGradeCount[progress.grade][nodeStatuses.CREDITED] = progress.progress_in_grade[nodeStatuses.CREDITED]?.length || 0;
          const sumValues = Object.values($progressByGradeCount[progress.grade]).reduce((a, b) => a + b, 0);
          $progressByGradeCount[progress.grade][nodeStatuses.NOT_STARTED] = progress.skill_total - sumValues;
          const masteryAchieved = progress.progress_in_grade[nodeStatuses.MASTERY_ACHIEVED]?.length || 0;
          const priorKnowledge = progress.progress_in_grade[nodeStatuses.PRIOR_KNOWLEDGE]?.length || 0;
          const credited = progress.progress_in_grade[nodeStatuses.CREDITED]?.length || 0;
          $completedSkillsCount[nodeStatuses.MASTERY_ACHIEVED] += masteryAchieved;
          $completedSkillsCount.total += masteryAchieved + priorKnowledge + credited;
        });
        nextState = deepAssign(nextState, sdoNs.PARENT_DASHBOARD, {
          $gradeHasActivity,
          $progressByGradeCount,
          $completedSkillsCount,
          $hasActivity,
        });
      }
      return nextState;
    },
    (nextState) => { // $progressByGrade
      const state = storeInstance.getState();
      const nextSubState = nextState[sdoNs.PARENT_DASHBOARD];
      const subState = state[sdoNs.PARENT_DASHBOARD];
      if (
        typeof nextSubState.$progressByGrade === 'undefined' ||
        nextSubState.statusesToShow !== subState.statusesToShow ||
        nextSubState.stats !== subState.stats
      ) {
        const $progressByGrade = {};
        Object.values(nextSubState.stats.progressByGrade).map((progress) => {
          if (typeof $progressByGrade[progress.grade] === 'undefined') {
            $progressByGrade[progress.grade] = {};
          }
          nextSubState.statusesToShow.map((status) => {
            $progressByGrade[progress.grade][status] = progress.progress_in_grade[status];
          });
        });
        nextState = deepAssign(nextState, sdoNs.PARENT_DASHBOARD, {
          $progressByGrade,
        });
      }
      return nextState;
    },
  ],
};

storeInstance.addState(sdo);
